import moment from "moment";
import { normalizeBadgeTone } from "../badgeStyles";

const toReadableLabel = (value) => {
  if (!value || typeof value !== "string") return "";
  return value
    .split("_")
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(" ");
};

const evaluationReasonLabels = {
  scheduled_review: "Scheduled review",
  care_plan_change: "Care plan change",
  incident: "Incident",
  request: "Request",
  other: "Other",
};

export const outcomeActionLabels = {
  no_change: "No change",
  care_plan_updated: "Care plan updated",
  follow_up_scheduled: "Follow-up scheduled",
  escalated: "Escalated",
  other: "Other",
};

const evidenceSourceShortLabels = {
  tool_snapshot: "Snapshot",
  observation_log: "Obs",
  incident: "Incident",
  safeguarding: "Safeguarding",
  warning_alert: "AI alert",
  anomaly_alert: "Anomaly",
  resident_care_plan: "Care plan",
  body_map: "Body map",
};

const evaluationTypeLabels = {
  goal_based: "Goal-based",
  section_based: "Section-based",
  overall_only: "Overall only",
  other: "Other",
};

const STATUS_TONE_MAP = {
  on_track: "info",
  approved: "success",
  completed: "success",
  fully_met: "success",
  met: "success",
  healthy: "success",
  partially_met: "warning",
  pending: "warning",
  watch: "warning",
  draft: "warning",
  not_met: "danger",
  rejected: "danger",
  critical: "danger",
};

export const getEvaluationStatusTone = (statusRaw) => {
  const key = normalizeBadgeTone(statusRaw || "");
  return STATUS_TONE_MAP[key] || "info";
};

export const formatEvaluationTimestamp = (value) => {
  if (!value) return "";
  const normalized =
    typeof value === "string" ? value.replace(" ", "T") : value;
  const parsed = moment(normalized);
  return parsed.isValid()
    ? parsed.format("MMM D, YYYY hh:mm A")
    : String(value);
};

export const formatEvaluationDate = (value) => {
  if (!value) return "";
  const normalized =
    typeof value === "string" ? value.replace(" ", "T") : value;
  const parsed = moment(normalized);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
};

/**
 * Normalize API / legacy evaluation items for CcEvaluationCard.
 */
export const getEvaluationDisplay = (item) => {
  const date = item?.evaluated_at ?? item?.date;
  const evaluator = item?.evaluated_by ?? item?.evaluatorName ?? "";
  const overallStatus = item?.overall_status ?? item?.overallStatus ?? "";
  const statusRaw = (overallStatus || "").toLowerCase().replace(/\s/g, "_");
  const reason =
    item?.evaluation_reason != null
      ? evaluationReasonLabels[item.evaluation_reason] ||
        toReadableLabel(item.evaluation_reason)
      : (item?.reason ?? "");
  const note = item?.evaluation_note ?? "";
  const nextDate = item?.next_evaluation_date ?? "";
  const evalType =
    item?.evaluation_json?.type != null
      ? evaluationTypeLabels[item.evaluation_json.type] ||
        toReadableLabel(item.evaluation_json.type)
      : "";
  const outcomeAction =
    item?.outcome_json?.action != null
      ? outcomeActionLabels[item.outcome_json.action] ||
        toReadableLabel(item.outcome_json.action)
      : "";

  // Per-goal attainment (goal_based) — flatten sections[].goals[] for display.
  const goals = [];
  const ej = item?.evaluation_json;
  if (ej?.type === "goal_based" && Array.isArray(ej.sections)) {
    ej.sections.forEach((sec) => {
      (sec?.goals || []).forEach((g) => {
        if (!g?.text && !g?.status) return;
        const statusKey = (g.status || "").toLowerCase().replace(/\s/g, "_");
        goals.push({
          text: g.text ?? "",
          status: g.status ? toReadableLabel(g.status) : "",
          statusTone: getEvaluationStatusTone(statusKey),
          note: g.progress_note ?? "",
        });
      });
    });
  }

  const footer = [];
  const nextFormatted = nextDate ? formatEvaluationDate(nextDate) : "";
  if (nextFormatted) {
    footer.push({ label: "Next evaluation", value: nextFormatted });
  }
  if (evalType) footer.push({ label: "Type", value: evalType });
  if (outcomeAction) footer.push({ label: "Outcome", value: outcomeAction });

  const sources = Array.isArray(item?.sources_json) ? item.sources_json : [];
  if (sources.length) {
    const counts = {};
    sources.forEach((s) => {
      const label =
        evidenceSourceShortLabels[s?.source_type] ||
        toReadableLabel(s?.source_type || "") ||
        "Other";
      counts[label] = (counts[label] || 0) + 1;
    });
    footer.push({
      label: "Evidence",
      value: Object.entries(counts)
        .map(([label, n]) => (n > 1 ? `${label} ×${n}` : label))
        .join(" · "),
    });
  }

  return {
    at: date,
    timestamp: formatEvaluationTimestamp(date),
    evaluator,
    status: overallStatus ? toReadableLabel(overallStatus) : "",
    statusTone: getEvaluationStatusTone(statusRaw),
    reason,
    note,
    goals,
    footer,
  };
};
